// external libraries
import { useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import classNames from "classnames";
import Lock01Icon from "@icons/lock-01.svg?react";
import Trash01Icon from "@icons/trash-01.svg?react";
import Home03Icon from "@icons/home-03.svg?react";
import ClipboardIcon from "@icons/clipboard.svg?react";

// hooks and state
import { useAppSelector } from "@/common/hooks/useRedux";
import { useAccountContext } from "@providers/AccountProvider";
import { useImpersonation } from "@/state/providers/ImpersonationProvider";
import { useLock } from "@/state/providers/LockProvider";
import { useDeviceAccessSettings } from "@/state/hooks/useDeviceAccess";
import { useFitText, FIT_TEXT_LINE_HEIGHT } from "@/common/hooks/useFitText";
import { useFitsInRow } from "@/common/hooks/useFitsInRow";

// components
import SearchBar from "@common/components/SearchBar";
import HDTabs from "@/common/components/HDTabs";
import ProfileMenu from "./ProfileMenu";
import ReleaseNotesMenu from "./ReleaseNotesMenu";
import HelpMenu from "./HelpMenu";
import { NAV_ICON_BUTTON_CLASS, NAV_ICON_BUTTON_CLASS_BASE, NAV_ICON_CLASS } from "./navIconButton";

// utilities
import { RecordingState, HDTab, VisitPage } from "@/common/utils/types";
import { HALLOWEEN_LOGO_ON_DARK_CLASS, HALLOWEEN_LOGO_SRC, isHalloween } from "@/common/utils/halloween";

const Navbar = () => {
    const { accountData } = useAccountContext();
    const { globalRecordingState } = useAppSelector((state) => state.globalState);
    const location = useLocation();
    const navigate = useNavigate();
    const { requestLock } = useLock();
    const { isImpersonating } = useImpersonation();
    const { data: deviceSettings } = useDeviceAccessSettings();
    const pinsEnabled = accountData?.pins_enabled === true;
    const showLockButton =
        pinsEnabled &&
        !isImpersonating &&
        (deviceSettings?.device_access_mode === "pin" || deviceSettings?.device_access_mode === "switch");

    const [isSearchBarVisible, setIsSearchBarVisible] = useState(false);

    // The clinic name is how a vet confirms which practice they are charting into, so it is
    // fitted rather than truncated: it shrinks, and wraps to a second line where the bar has
    // the height to spare, before it is given up altogether.
    const clinicName = accountData?.clinic_name || "";
    // 13px is the smallest size worth reading here. Below that the name is dropped
    // from the bar rather than shown clipped - it stays in the profile menu, which is
    // where a vet confirming which clinic they are charting into can still find it.
    const {
        ref: clinicNameRef,
        fontSize: clinicNameSize,
        maxHeight: clinicNameMaxHeight,
        fits: clinicNameFits,
    } = useFitText(clinicName, 20, 13);

    // The secondary icons outlast everything else in the bar and go last, so they are
    // kept while the row can hold them rather than dropped at a breakpoint.
    const {
        rowRef: navRowRef,
        itemRef: secondaryIconsRef,
        fixedRef: navFixedRef,
        visible: showSecondaryIcons,
    } = useFitsInRow();

    // Build tabs based on current location
    const tabs = useMemo<HDTab[]>(() => {
        const baseTabs: HDTab[] = [
            {
                header: "Dashboard",
                isActive: location.pathname === "/dashboard",
                id: "/dashboard",
                icon: Home03Icon,
            },
            {
                header: "Visits",
                isActive: location.pathname === "/",
                id: "/",
                icon: ClipboardIcon,
            },
        ];

        return baseTabs;
    }, [location.pathname]);

    /**
     * Change the tab and navigate to the selected route
     * @param id - The id of the tab
     */
    const changeTab = (id: string) => {
        if (location.pathname === id.split("?")[0]) {
            return;
        }

        // Only the Visits tab uses the ?page= view param (list vs calendar).
        // Navigate to every other tab cleanly so the param doesn't leak onto
        // routes that don't read it (e.g. /dashboard).
        if (id === "/") {
            const params = new URLSearchParams(location.search);
            const view =
                accountData?.meta_data?.default_view === VisitPage.Calendar ? VisitPage.Calendar : VisitPage.List;
            params.set("page", view);
            navigate({ pathname: id, search: params.toString() });
        } else {
            navigate(id);
        }
    };

    const toggleSearchBar = () => {
        setIsSearchBarVisible(!isSearchBarVisible);
    };

    const isRecording =
        globalRecordingState === RecordingState.recording || globalRecordingState === RecordingState.paused;

    const halloween = isHalloween();

    const logoContent = (
        <>
            {halloween ? (
                // Outline + glow keep the art's dark hat and bat visible on the dark bar, as on the lock screen.
                <img
                    src={HALLOWEEN_LOGO_SRC}
                    alt="logo"
                    className={`hidden sm:inline h-10 w-auto ${HALLOWEEN_LOGO_ON_DARK_CLASS}`}
                />
            ) : (
                <img src="/happydoc.svg" alt="logo" className="hidden sm:inline h-8 w-auto  " />
            )}
            <img src="/favicon.png" alt="logo" className="sm:hidden h-8 w-auto " />
        </>
    );

    return (
        <div className="flex-col bg-gray-100">
            <div
                ref={navRowRef}
                className={classNames(
                    // A 64px floor at every width, not just `lg`. This used to be `lg:h-16` with
                    // `py-2`, so the bar was 64px above 1024px and 75px below it - the same bar,
                    // two heights. The tab strip measures 58px, so the floor is what sets the
                    // height here and the strip sits inside it (`self-stretch` + `items-end` on
                    // its wrapper), which is what puts HDTabs' own `border-b` track on this
                    // element's border - it offsets itself by `-mb-px` expecting exactly that,
                    // so the active tab's underline replaces a segment of the navbar rule
                    // instead of floating above it as a second, shorter line.
                    "flex flex-wrap lg:flex-nowrap min-h-16 shrink-0 items-center border-b px-2 sm:px-4 py-0 gap-x-2 sm:gap-x-5 gap-y-2",
                    // EN-1476: the Halloween night bar. Tabs and icon buttons are recolored by the
                    // scoped `.halloween-nav` rules in index.css.
                    halloween ? "halloween-nav bg-[#241634] border-[#3b2352]" : "border-gray-200 bg-gray-100",
                )}
            >
                <div className="flex items-center self-stretch flex-1 min-w-0 gap-x-2 sm:gap-x-5">
                    {isRecording ? (
                        <span ref={navFixedRef} className="focused shrink-0">
                            {logoContent}
                        </span>
                    ) : (
                        <Link
                            ref={navFixedRef}
                            to="/"
                            className="focused no-underline text-inherit shrink-0"
                            aria-label="Home"
                        >
                            {logoContent}
                        </Link>
                    )}
                    <h1
                        ref={clinicNameRef}
                        className={classNames(
                            // `break-words` matches the fitting probe, which measures with
                            // `overflow-wrap: break-word`. Without it a single word longer
                            // than the space left wraps in the probe and so reports as
                            // fitting, then refuses to wrap here and is silently cut off by
                            // `overflow-hidden` - a clipped name, which is the one thing the
                            // measurement exists to avoid. Breaking a long word is the lesser
                            // loss, and it is what was measured.
                            "min-w-0 shrink overflow-hidden break-words font-semibold",
                            halloween ? "text-white" : "text-gray-900",
                            // sr-only rather than unmounted: the heading stays in the
                            // accessibility tree, and the hook keeps measuring so the name
                            // returns as soon as the viewport gives it room again.
                            !clinicNameFits && "sr-only",
                        )}
                        style={{
                            fontSize: `${clinicNameSize}px`,
                            lineHeight: FIT_TEXT_LINE_HEIGHT,
                            maxHeight: clinicNameMaxHeight,
                        }}
                        title={clinicName}
                    >
                        {clinicName}
                    </h1>
                    {/* Stretched to the bar's full content height and bottom-aligned, so the
                        tab strip's own border-b lands exactly on the navbar's border and the
                        active tab's underline replaces a segment of it. Centring instead only
                        ever lined up by accident, when the strip happened to be a couple of
                        pixels taller than the bar. `marginTop` is cleared for the same reason:
                        it pushes the strip down past the border. */}
                    {/* Hidden by CSS at the widths where the mobile search bar actually takes
                        the row, not unmounted on the flag. The flag is only clearable from the
                        close button inside that bar, which is itself `lg:hidden`: opening
                        search below `lg` and then widening past it left the flag set with
                        nothing able to clear it, so Dashboard and Visits stayed gone on a
                        desktop window that had room for them and the search field beside them.
                        Keyed off the same breakpoint SearchBar uses, so the two cannot drift. */}
                    <div
                        ref={navFixedRef}
                        className={classNames(
                            "shrink-0 self-stretch items-end",
                            isSearchBarVisible ? "hidden lg:flex" : "flex",
                        )}
                    >
                        <HDTabs
                            tabs={tabs}
                            changeTab={changeTab}
                            disabled={isRecording}
                            paddingVertical="py-[18px]"
                            marginTop=""
                            // The icons are always on, and the labels drop below 390px,
                            // where they are what stops the bar fitting on one line. The
                            // threshold accounts for the icons themselves: they widen the
                            // strip by ~40px, so labels have to go sooner than they would
                            // without them. sr-only rather than hidden, so the tab keeps
                            // its accessible name and is still announced.
                            labelClassName="max-[389px]:sr-only"
                            // A 16px icon reads correctly beside 14px text, but on its own
                            // it is the only thing identifying the tab, so it steps up.
                            iconClassName="max-[389px]:w-6 max-[389px]:h-6"
                        />
                    </div>
                </div>

                <div ref={navFixedRef} className="flex items-center gap-x-2 w-auto justify-end ml-auto">
                    <div className={classNames("flex")}>
                        <SearchBar
                            isSearchBarVisible={isSearchBarVisible}
                            toggleSearchBar={toggleSearchBar}
                            disabled={isRecording}
                        />
                    </div>

                    {showLockButton && (
                        <button
                            type="button"
                            onClick={() => requestLock()}
                            className={NAV_ICON_BUTTON_CLASS}
                            title="Lock screen (⌘⇧L)"
                            aria-label="Lock screen (⌘⇧L)"
                        >
                            <Lock01Icon className={NAV_ICON_CLASS} />
                        </button>
                    )}

                    {/* Kept in the bar as long as it can hold them, and the last thing to go.
                        The recycle bin and release notes also live in the profile menu, so those
                        two only lose their shortcut. Product tours is only here, so on a narrow
                        enough viewport it is genuinely unavailable - accepted deliberately rather
                        than crowding the bar to avoid it. */}
                    <div
                        ref={secondaryIconsRef}
                        className={classNames("items-center gap-x-2", showSecondaryIcons ? "flex" : "hidden")}
                    >
                        {/* Settings is a page-level exit: navigating away mid-recording is how
                        recordings get stranded, which is why the logo, the main tabs and the search bar
                        all go inert while recording, and why the account menu (the only other route to
                        Settings) disables itself. This link has to hold the same line - as a disabled
                        button rather than a hidden one, so the affordance does not move. */}
                        {isRecording ? (
                            <button
                                type="button"
                                disabled
                                className={classNames(NAV_ICON_BUTTON_CLASS_BASE, "opacity-50 cursor-not-allowed")}
                                title="Finish the recording to open the Recycle Bin"
                                aria-label="Recycle Bin (unavailable while recording)"
                            >
                                <Trash01Icon className={NAV_ICON_CLASS} />
                            </button>
                        ) : (
                            <Link
                                to="/settings?tab=recycle-bin"
                                className={NAV_ICON_BUTTON_CLASS}
                                title="Recycle Bin"
                                aria-label="Recycle Bin"
                            >
                                <Trash01Icon className={NAV_ICON_CLASS} />
                            </Link>
                        )}

                        <ReleaseNotesMenu />

                        <HelpMenu />
                    </div>

                    <ProfileMenu />
                </div>
            </div>
        </div>
    );
};
export default Navbar;
